import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';

export const Container: React.FC<{ children: React.ReactNode; className?: string; narrow?: boolean }> = ({
  children,
  className,
  narrow,
}) => <div className={cn('mx-auto w-full px-5 sm:px-6', narrow ? 'max-w-3xl' : 'max-w-6xl', className)}>{children}</div>;

export const Section: React.FC<{
  children: React.ReactNode;
  className?: string;
  id?: string;
  narrow?: boolean;
  surface?: boolean;
}> = ({ children, className, id, narrow, surface }) => (
  <section id={id} className={cn('relative py-20 md:py-28', surface && 'border-y border-line bg-surface/40', className)}>
    <Container narrow={narrow}>{children}</Container>
  </section>
);

export const Eyebrow: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => (
  <p className={cn('eyebrow mb-5', className)}>
    <span className="h-px w-6 bg-primary/60" aria-hidden="true" />
    {children}
  </p>
);

export const SectionHeading: React.FC<{
  eyebrow?: string;
  title: React.ReactNode;
  lead?: React.ReactNode;
  center?: boolean;
  className?: string;
  as?: 'h1' | 'h2';
}> = ({ eyebrow, title, lead, center, className, as: H = 'h2' }) => (
  <div className={cn('max-w-3xl', center && 'mx-auto text-center', className)}>
    {eyebrow && <Eyebrow className={center ? 'justify-center' : undefined}>{eyebrow}</Eyebrow>}
    <H className={cn('font-semibold text-white', H === 'h1' ? 'text-4xl sm:text-5xl md:text-6xl' : 'text-3xl md:text-[2.75rem] md:leading-[1.1]')}>
      {title}
    </H>
    {lead && <p className="mt-5 text-lg leading-relaxed text-muted">{lead}</p>}
  </div>
);

export const ButtonLink: React.FC<{
  to: string;
  children: React.ReactNode;
  variant?: 'primary' | 'secondary';
  className?: string;
  arrow?: boolean;
}> = ({ to, children, variant = 'primary', className, arrow }) => (
  <Link to={to} className={cn(variant === 'primary' ? 'btn-primary' : 'btn-secondary', 'group', className)}>
    {children}
    {arrow && <ArrowRight className="size-4 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />}
  </Link>
);

/** Inline-Link mit Pfeil für "Mehr erfahren"-Verweise */
export const ArrowLink: React.FC<{ to: string; children: React.ReactNode; className?: string }> = ({ to, children, className }) => (
  <Link to={to} className={cn('group inline-flex items-center gap-1.5 font-medium text-primary', className)}>
    {children}
    <ArrowRight className="size-4 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
  </Link>
);

/** LinkedIn-Marke: Lucide führt seit v1 keine Markenlogos mehr */
export const LinkedInIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M20.45 20.45h-3.56v-5.57c0-1.33-.02-3.04-1.85-3.04-1.85 0-2.14 1.45-2.14 2.94v5.67H9.34V9h3.42v1.56h.05c.48-.9 1.64-1.85 3.37-1.85 3.6 0 4.27 2.37 4.27 5.46v6.28ZM5.34 7.43a2.06 2.06 0 1 1 0-4.13 2.06 2.06 0 0 1 0 4.13ZM7.12 20.45H3.56V9h3.56v11.45ZM22.22 0H1.77C.79 0 0 .77 0 1.73v20.54C0 23.23.79 24 1.77 24h20.45c.98 0 1.78-.77 1.78-1.73V1.73C24 .77 23.2 0 22.22 0Z" />
  </svg>
);
